/** Arranque: sesión, enrutado entre galería y tablero. */
import './styles.css'
import { api, UnauthorizedError, type SessionInfo } from './api.ts'
import { App } from './app.ts'
import { h, toast } from './ui/dom.ts'
import { icon } from './ui/icons.ts'
import { Gallery } from './ui/gallery.ts'
import { openNameDialog } from './ui/dialogs.ts'
import { renderLogin as showLogin } from './ui/login.ts'

/* El modo oscuro se aplica antes de pintar nada: si se esperara a abrir el
   tablero, la galería y el arranque destellarían en blanco. */
try {
  const prefs = JSON.parse(localStorage.getItem('wb.prefs.v1') ?? '{}') as { darkMode?: boolean }
  if (prefs.darkMode) document.body.classList.add('theme-dark')
} catch {
  /* modo claro */
}

const root = document.getElementById('app')!
let session: SessionInfo | null = null
let currentApp: App | null = null
let gallery: Gallery | null = null

function boardIdFromPath(): string | null {
  const match = location.pathname.match(/^\/b\/([A-Za-z0-9_-]+)/)
  return match ? match[1] : null
}

function navigate(path: string, replace = false): void {
  if (replace) history.replaceState(null, '', path)
  else history.pushState(null, '', path)
  void route()
}

/**
 * Cada navegación lleva un número; una respuesta que llega tarde (red lenta y
 * el usuario ya fue a otro sitio) se descarta. Antes montaba el tablero encima
 * de la galería o dejaba un tablero huérfano conectado para siempre.
 */
let routeSeq = 0

async function route(): Promise<void> {
  const seq = ++routeSeq
  if (!session?.authenticated) {
    renderLogin()
    return
  }
  const boardId = boardIdFromPath()
  if (currentApp) {
    currentApp.destroy()
    currentApp = null
  }
  root.replaceChildren()

  if (boardId) {
    await openBoard(boardId, seq)
  } else {
    if (!gallery) {
      gallery = new Gallery({
        userName: session.name,
        onOpenBoard: (id) => navigate(`/b/${id}`),
        onChangeName: () => changeName(),
      })
    }
    gallery.setUserName(session.name)
    root.append(gallery.el)
    await gallery.refresh()
    if (seq !== routeSeq) return
  }
}

async function openBoard(boardId: string, seq: number): Promise<void> {
  let readOnly = false
  let kind: 'board' | 'mindmap' | undefined
  let name: string | undefined
  try {
    const board = await api.getBoard(boardId)
    readOnly = board.role === 'view'
    kind = board.kind
    name = board.name
    if (seq !== routeSeq) return
    document.title = `${board.name} · Pizarra La Unitec`
  } catch (error) {
    if (seq !== routeSeq) return
    if (error instanceof UnauthorizedError) {
      session = null
      renderLogin()
      return
    }
    toast('No se encontró esa pizarra')
    navigate('/', true)
    return
  }

  if (seq !== routeSeq) return
  currentApp = new App({
    boardId,
    userId: session!.userId,
    userName: session!.name,
    readOnly,
    kind,
    name,
    onExit: () => navigate('/'),
  })
  root.append(currentApp.el)
}

function changeName(): void {
  openNameDialog(session?.name ?? '', async (name) => {
    localStorage.setItem('wb.name', name)
    try {
      session = await api.setName(name)
    } catch {
      /* se mantiene el nombre local */
    }
    gallery?.setUserName(name)
    await route()
  })
}

function renderLogin(): void {
  void showLogin(root, {
    onSuccess: (info) => {
      session = info
      void route()
    },
  })
}

window.addEventListener('popstate', () => void route())

async function boot(): Promise<void> {
  try {
    session = await api.session()
  } catch {
    session = null
  }
  // Los enlaces compartidos traen ?t=token: el servidor ya lo canjeó por una cookie.
  if (location.search.includes('t=')) {
    const url = new URL(location.href)
    url.searchParams.delete('t')
    history.replaceState(null, '', url.pathname + url.search)
  }
  await route()
}

void boot()
